import { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import AccessPanel from "admin/components/user-management/AccessPanel";
import AdminGrantsPanel from "admin/components/user-management/AdminGrantsPanel";
import { GROUP_DRAWER_TABS } from "admin/components/user-management/Groups";
import UserAutocomplete from "admin/components/user-management/UserAutocomplete";
import useCanEditAdminApp from "admin/hooks/useCanEditAdminApp";
import { useCanViewAdminGrants } from "admin/hooks/useCanViewAdminGrants";
import useGroupAccess from "admin/hooks/useGroupAccess";
import useGroups from "admin/hooks/useGroups";
import { useManagerRoles } from "admin/hooks/useManagerRoles";
import { usePendingGrants } from "admin/hooks/usePendingGrants";
import useUsers from "admin/hooks/useUsers";
import { GrantListResponse, GrantPermission, GrantSource } from "admin/types/interfaces/grant";
import User, { UserGroup } from "admin/types/interfaces/user";
import { getUserNameByEmail } from "admin/utils/users";
import { Users, Trash2, Loader2, Calendar, User as UserIcon } from "lucide-react";
import { PersonAvatar } from "~/components/PersonAvatar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "~/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { Textarea } from "~/components/ui/textarea";
import useToast from "~/components/ui/toast";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { useFetcher } from "~/hooks/useFetcher";
import {
  getStatusBadgeVariant,
  UserStatusFilter,
} from "~/pages/admin/components/user-management/UserTable";
import useGroupUsers from "~/pages/admin/hooks/useGroupUsers";
import { ACCESS_CONTROLLER_V1 } from "~/utils/accessController";
import { trackEvent } from "~/utils/monitoring";

const { VITE_ACCESS_CONTROLLER_API } = import.meta.env;

interface GroupFormData {
  display_name: string;
  description: string;
  users: User[];
}

interface GroupDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  group: UserGroup;
  defaultTab?: GROUP_DRAWER_TABS;
  availableUsers?: User[];
}

const GroupDrawer = ({
  isOpen,
  onClose,
  group,
  defaultTab = GROUP_DRAWER_TABS.details,
}: GroupDrawerProps) => {
  const { showSuccess, showError } = useToast();
  const {
    data: groupUsers = [],
    isLoading: isLoadingUsers,
    mutate: mutateGroupUsers,
  } = useGroupUsers({ groupName: group.name, activeOnly: false });
  const [activeTab, setActiveTab] = useState<GROUP_DRAWER_TABS>(defaultTab);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [statusFilter, setStatusFilter] = useState<UserStatusFilter>(UserStatusFilter.All);

  const canEdit = useCanEditAdminApp();
  const canEditGroup = group.actions_permitted
    ? group.actions_permitted.includes(GrantPermission.can_edit)
    : canEdit;
  const canManage = group.actions_permitted
    ? group.actions_permitted.includes(GrantPermission.can_manage)
    : canEdit;
  const showAdminGrants = useCanViewAdminGrants();

  const GROUP_URL = `${ACCESS_CONTROLLER_V1}/user-tags/${group.name}`;

  const { data: users = [] } = useUsers();
  const { mutate } = useGroups();
  const {
    data: accessData,
    isLoading: isLoadingAccess,
    error: accessDataError,
  } = useGroupAccess(group.name);

  const {
    data: grantsData,
    isLoading: grantsLoading,
    error: grantsError,
    mutate: mutateGrants,
  } = useFetcher<GrantListResponse>(`${VITE_ACCESS_CONTROLLER_API}/user-tags/${group.name}/grants`);

  const {
    pendingGrantAdditions,
    pendingGrantRemovals,
    pendingGrantUpdates,
    hasGrantChanges,
    resetGrants,
    onAddAccess,
    onRemoveAccess,
    onUpdateAccess,
    flushGrants,
  } = usePendingGrants();

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, dirtyFields },
    setValue,
    watch,
    reset,
  } = useForm<GroupFormData>({
    mode: "onChange",
    defaultValues: {
      display_name: group.display_name || group.name,
      description: group.description || "",
      users: groupUsers,
    },
  });

  const watchedUsers = watch("users");
  const canDelete = group.user_count === 0;

  const filteredUsers = useMemo(() => {
    if (statusFilter === UserStatusFilter.All) {
      return watchedUsers;
    } else if (statusFilter === UserStatusFilter.Enabled) {
      return watchedUsers.filter((user) => user.is_active);
    } else {
      return watchedUsers.filter((user) => !user.is_active);
    }
  }, [watchedUsers, statusFilter]);

  const groupUserEmails = useMemo(
    () =>
      groupUsers
        ?.map((u) => u.email)
        .sort()
        .join(",") ?? "",
    [groupUsers]
  );

  // Lowercase the ids: useManagerRoles compares this set against case-preserving member
  // emails, and user: principal ids are case-insensitive (the server returns them lowercase).
  const currentManagerEmails = useMemo(
    () =>
      new Set(
        (grantsData?.items ?? [])
          .filter(
            (g) =>
              g.permission === GrantPermission.can_manage &&
              g.source === GrantSource.direct &&
              g.principal.startsWith("user:")
          )
          .map((g) => g.principal.slice("user:".length).toLowerCase())
      ),
    [grantsData]
  );

  const {
    getRoleFor: getMemberRole,
    isRemoved: isMemberRemoved,
    setRole: setManagerRole,
    markRemoved: markMemberRemoved,
    clearRole: clearManagerRole,
    reset: resetManagerRoles,
    hasChanges: hasManagerChanges,
    flush: flushManagerRoles,
    removedEmails: pendingMemberRemovals,
  } = useManagerRoles(currentManagerEmails);

  // Members already saved to the group — used to decide whether "Remove" is a reversible
  // pending removal (existing member, row stays) or an immediate drop (unsaved addition).
  const originalMemberEmails = useMemo(() => new Set(groupUsers.map((u) => u.email)), [groupUsers]);

  // Update default active tab and reset pending grants when drawer opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab(
        defaultTab === GROUP_DRAWER_TABS.administration && !showAdminGrants
          ? GROUP_DRAWER_TABS.details
          : defaultTab
      );
      resetGrants();
      resetManagerRoles();
    }
  }, [isOpen, defaultTab, showAdminGrants, resetGrants, resetManagerRoles]);

  // Reset form when drawer opens or group data changes
  useEffect(() => {
    if (!isOpen) return;
    reset({
      display_name: group.display_name || group.name,
      description: group.description || "",
      users: groupUsers,
    });
    setStatusFilter(UserStatusFilter.All);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, group.name, group.display_name, group.description, groupUserEmails]);

  const handleDeleteGroup = async () => {
    setIsDeleting(true);
    try {
      const response = await fetch(GROUP_URL, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        showError("Failed to delete group, please try again.");
      } else {
        showSuccess("Group deleted successfully");
        await mutate();
        onClose();
        setShowDeleteDialog(false);
      }
    } catch (error) {
      showError("Failed to delete group, please try again");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if ((isDirty || hasGrantChanges || hasManagerChanges) && canEditGroup) {
      setShowUnsavedDialog(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowUnsavedDialog(false);
    reset();
    onClose();
  };

  const handleCancelClose = () => {
    setShowUnsavedDialog(false);
  };

  const updateGroupUsers = async (userEmails: string[]) => {
    const response = await fetch(`${GROUP_URL}/users`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ users: userEmails }),
    });
    if (!response.ok) {
      throw new Error("Failed to update group users");
    }
  };

  const onSubmit = async (formData: GroupFormData) => {
    setIsSubmitting(true);

    // Pending member removals (Members-tab dropdown) are tracked separately from the form,
    // so the row stays visible until Save — exclude them from the persisted membership here.
    const updatedUsersList = formData.users
      .filter((user) => !isMemberRemoved(user.email))
      .map((user) => user.email);

    try {
      if (dirtyFields.users || pendingMemberRemovals.size > 0) {
        await updateGroupUsers(updatedUsersList);
      }

      if (dirtyFields.display_name || dirtyFields.description) {
        const response = await fetch(GROUP_URL, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...(dirtyFields.display_name && {
              display_name: formData.display_name,
            }),
            ...(dirtyFields.description && {
              description: formData.description,
            }),
          }),
        });
        if (!response.ok) {
          throw new Error();
        }
      }
      await mutate();
      await mutateGroupUsers();
    } catch (error) {
      showError("Failed to update group, please try again");
      setIsSubmitting(false);
      return;
    }

    const grantsUrl = `${VITE_ACCESS_CONTROLLER_API}/user-tags/${group.name}/grants`;
    const allFailedLabels: string[] = [];

    const { failedLabels: managerFailedLabels } = await flushManagerRoles(
      grantsUrl,
      formData.users
    );
    allFailedLabels.push(...managerFailedLabels);

    const { failedLabels } = await flushGrants(grantsUrl);
    allFailedLabels.push(...failedLabels);

    await mutateGrants();
    setIsSubmitting(false);

    if (allFailedLabels.length > 0) {
      showError(`Failed to save grants for: ${allFailedLabels.join(", ")}`);
    } else {
      showSuccess("Group updated successfully");
      onClose();
    }
  };

  const handleRemoveUser = (userEmail: string) => {
    const updatedUsers = watchedUsers.filter((user) => user.email !== userEmail);
    setValue("users", updatedUsers, { shouldDirty: true });
  };

  const handleAddUser = (user: User) => {
    setValue("users", [...watchedUsers, user], { shouldDirty: true });
  };

  const handleMemberRoleChange = (email: string, value: "manager" | "member" | "remove") => {
    if (value === "remove") {
      if (originalMemberEmails.has(email)) {
        // Existing member: keep the row as a reversible pending removal until Save.
        markMemberRemoved(email);
      } else {
        // Unsaved addition: drop it immediately (reversible by re-adding).
        handleRemoveUser(email);
        clearManagerRole(email);
      }
    } else {
      setManagerRole(email, value);
    }
  };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={handleClose}>
        <SheetContent
          className="w-[750px] sm:max-w-[750px] ml-32 bg-background flex flex-col h-screen gap-0"
          onOpenAutoFocus={(e) => {
            // Prevent auto-focusing on active tab trigger
            e.preventDefault();
          }}
        >
          <SheetHeader className="flex-shrink-0 pl-8 pr-16 mb-4">
            <SheetTitle className="flex items-center gap-3 text-2xl">
              <Users className="h-5 w-5" />
              {group.display_name || group.name}
            </SheetTitle>
          </SheetHeader>

          <div className="flex-1 flex flex-col overflow-hidden">
            <Tabs
              value={activeTab}
              onValueChange={(value: string) => setActiveTab(value as GROUP_DRAWER_TABS)}
              className="w-full flex-1 flex flex-col overflow-hidden"
            >
              <div className="mx-8">
                <TabsList>
                  <TabsTrigger value={GROUP_DRAWER_TABS.details}>Group Details</TabsTrigger>
                  <TabsTrigger value={GROUP_DRAWER_TABS.members}>
                    Members ({isLoadingUsers ? "-" : watchedUsers.length})
                  </TabsTrigger>
                  <TabsTrigger
                    value={GROUP_DRAWER_TABS.access}
                    onClick={() => trackEvent("admin_access_tab_clicked", { type: "group" })}
                  >
                    Access
                  </TabsTrigger>
                  {showAdminGrants && (
                    <TabsTrigger value={GROUP_DRAWER_TABS.administration}>
                      Administration
                    </TabsTrigger>
                  )}
                </TabsList>
              </div>
              <div className="flex-1 overflow-y-auto px-8 pb-6">
                <form>
                  <TabsContent value={GROUP_DRAWER_TABS.details} className="space-y-6 pl-0 mt-6">
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="groupName">Group name</Label>
                          <Input
                            id="groupName"
                            {...register("display_name", {
                              required: "Group name is required",
                            })}
                            disabled={!canEditGroup}
                            placeholder="Enter group name"
                            className={errors.display_name ? "border-destructive" : ""}
                          />
                          {errors.display_name && (
                            <p className="text-sm text-destructive">
                              {errors.display_name.message}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="description">Description</Label>
                        <Textarea
                          id="description"
                          {...register("description")}
                          disabled={!canEditGroup}
                          placeholder="Enter group description"
                          rows={4}
                          className={errors.description ? "border-destructive" : ""}
                        />
                        {errors.description && (
                          <p className="text-sm text-destructive">{errors.description.message}</p>
                        )}
                      </div>

                      {(group.created_by || group.created_at) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {group.created_at && (
                            <div className="space-y-2">
                              <Label>Created on</Label>
                              <div className="relative">
                                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                                <Input
                                  value={new Date(group.created_at || "").toLocaleDateString()}
                                  disabled={true}
                                  className="bg-background border-border pl-10"
                                />
                              </div>
                            </div>
                          )}
                          {group.created_by && (
                            <div className="space-y-2">
                              <Label>Created by</Label>
                              <div className="relative">
                                <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                                <Input
                                  value={getUserNameByEmail(group.created_by, users)}
                                  disabled={true}
                                  className="bg-background border-border pl-10"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value={GROUP_DRAWER_TABS.members} className="space-y-6 pl-0 mt-6">
                    <div className="flex items-center justify-between border-b border-border pb-2">
                      <Label>Members</Label>
                      <Select
                        value={statusFilter}
                        onValueChange={(value) => setStatusFilter(value as UserStatusFilter)}
                      >
                        <SelectTrigger className="w-32 h-9 border border-border bg-background">
                          <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={UserStatusFilter.All}>All Statuses</SelectItem>
                          <SelectItem value={UserStatusFilter.Enabled}>Enabled</SelectItem>
                          <SelectItem value={UserStatusFilter.Disabled}>Disabled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {canEditGroup && (
                      <UserAutocomplete
                        handleSelectUser={handleAddUser}
                        selectedUsers={watchedUsers}
                        autoRefocus
                      />
                    )}
                    {isLoadingUsers && (
                      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                          <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
                        </div>
                        <h3 className="text-lg font-medium text-foreground mb-2">
                          Loading Users...
                        </h3>
                      </div>
                    )}
                    {!isLoadingUsers && filteredUsers.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                        {statusFilter === UserStatusFilter.All && (
                          <>
                            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                              <Users className="w-8 h-8 text-muted-foreground" />
                            </div>
                            <h3 className="text-lg font-medium text-foreground mb-2">No Members</h3>
                          </>
                        )}
                        <p className="text-sm text-muted-foreground">
                          {statusFilter === UserStatusFilter.All
                            ? `This group doesn't have any members yet. ${
                                canEditGroup ? "Add users using the search above." : ""
                              } `
                            : `No ${
                                statusFilter === UserStatusFilter.Enabled ? "enabled" : "disabled"
                              } users in this group. `}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2 mb-2">
                        {filteredUsers.map((user) => (
                          <div
                            key={user.email}
                            className="flex items-center justify-between p-2 rounded-lg hover:bg-muted transition-colors"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <PersonAvatar
                                name={user.full_name || user.email}
                                email={user.email}
                                imageUrl={user.image_url}
                              />
                              <div className="flex-1 min-w-0 flex gap-2 items-center">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="font-medium text-sm text-foreground truncate max-w-44 flex-shrink-0">
                                      {user.full_name || "No name"}
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent>{user.full_name || "No name"}</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="text-sm text-muted-foreground truncate min-w-0 flex-1">
                                      {user.email}
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent>{user.email}</TooltipContent>
                                </Tooltip>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {!user.is_active && (
                                <Badge variant={getStatusBadgeVariant(user.is_active)}>
                                  Disabled
                                </Badge>
                              )}
                              {canEditGroup ? (
                                <Select
                                  value={
                                    grantsLoading || grantsError
                                      ? undefined
                                      : isMemberRemoved(user.email)
                                        ? "remove"
                                        : getMemberRole(user.email)
                                  }
                                  onValueChange={(value) =>
                                    handleMemberRoleChange(
                                      user.email,
                                      value as "manager" | "member" | "remove"
                                    )
                                  }
                                  disabled={grantsLoading || !!grantsError}
                                >
                                  <SelectTrigger className="w-[110px]">
                                    <SelectValue placeholder={grantsError ? "Unavailable" : "…"} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="manager">Manager</SelectItem>
                                    <SelectItem value="member">Member</SelectItem>
                                    <SelectItem
                                      value="remove"
                                      className="text-destructive focus:text-destructive"
                                    >
                                      Remove
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : (
                                getMemberRole(user.email) === "manager" && (
                                  <span className="text-sm text-muted-foreground">Manager</span>
                                )
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                  <TabsContent value={GROUP_DRAWER_TABS.access} className="space-y-6 pl-0 mt-6">
                    <AccessPanel
                      accessData={accessData}
                      isLoading={isLoadingAccess}
                      error={accessDataError}
                      context="group"
                    />
                  </TabsContent>
                  {showAdminGrants && (
                    <TabsContent value={GROUP_DRAWER_TABS.administration} className="pl-0">
                      <AdminGrantsPanel
                        grantsUrl={`${VITE_ACCESS_CONTROLLER_API}/user-tags/${group.name}/grants`}
                        resourceType="group"
                        pendingAdditions={pendingGrantAdditions}
                        pendingRemovals={pendingGrantRemovals}
                        pendingUpdates={pendingGrantUpdates}
                        onAddAccess={onAddAccess}
                        onRemoveAccess={onRemoveAccess}
                        onUpdateAccess={onUpdateAccess}
                        canEdit={canManage && canEdit}
                      />
                    </TabsContent>
                  )}
                </form>
              </div>
            </Tabs>
          </div>

          {canEditGroup && (
            <div className="flex-shrink-0 bg-background border-t border-border py-4 px-8">
              <div className="flex items-center justify-between gap-2">
                <div>
                  {canDelete && (
                    <Button variant="destructive" onClick={() => setShowDeleteDialog(true)}>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Group
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {(isDirty || hasGrantChanges || hasManagerChanges) && (
                    <Button variant="outline" onClick={handleClose}>
                      Cancel
                    </Button>
                  )}
                  <Button
                    onClick={() => {
                      trackEvent("admin_group_save_clicked");
                      handleSubmit(onSubmit)();
                    }}
                    disabled={(!isDirty && !hasGrantChanges && !hasManagerChanges) || isSubmitting}
                  >
                    {isSubmitting ? "Saving..." : "Save"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to close without saving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelClose}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmClose}
              className="bg-destructive hover:bg-destructive/90"
            >
              Close without saving
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Group</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{group.display_name}&quot;? This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteGroup}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting ? "Deleting..." : "Delete Group"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default GroupDrawer;
